import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/core";

export interface CartPanelHeaderProps {
  /** Artículos, no líneas. */
  count: number;
  titleId: string;
  onClose: () => void;
}

/**
 * La cabecera del panel del carrito.
 *
 * El título lleva `id` porque es lo que apunta el `aria-labelledby` del
 * diálogo: así el lector de pantalla anuncia "Mi carrito, diálogo" al
 * abrirlo, en vez de dejar a la persona dentro de una región sin nombre.
 *
 * El conteo va en una línea aparte y no pegado al título ("Mi carrito
 * (3)"): el título se lee al abrir y cambia el número en cada pulsación
 * del "+", lo que haría que el lector reanunciara el diálogo entero.
 */
export function CartPanelHeader({ count, titleId, onClose }: CartPanelHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="flex shrink-0 items-start justify-between gap-4 border-b border-ds-border px-5 py-4">
      <div>
        <h2 id={titleId} className="text-[17px] font-semibold tracking-tight text-ds-ink">
          {t("Mi carrito")}
        </h2>
        <p aria-live="polite" className="mt-0.5 text-[12px] text-ds-ink-3">
          {count === 1 ? t("{{n}} artículo", { n: count }) : t("{{n}} artículos", { n: count })}
        </p>
      </div>

      <Button
        variant="ghost"
        size="sm"
        iconOnly
        leftIcon={<X />}
        onClick={onClose}
        aria-label={t("Cerrar el carrito")}
      />
    </div>
  );
}
